import React, { useState } from 'react';
import { Zap, Calendar, ArrowLeft, Clock, AlertTriangle, TrendingUp, CheckCircle2, Receipt, Download, X, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
interface UsageScreenProps {
  onNavigate?: (screen: string) => void;
}
interface Invoice {
  id: string;
  date: string;
  amount: string;
  desc: string;
  minutes: number;
  included: number;
  overage: number;
  planName: string;
  rate: number;
  basePrice: number;
}
export function UsageScreen({
  onNavigate
}: UsageScreenProps) {
  const {
    isLight
  } = useTheme();
  // Simulation state
  const [planMode, setPlanMode] = useState<'free' | 'pro'>('free');
  const [simOverage, setSimOverage] = useState(false);
  // Invoice state
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [downloadedIds, setDownloadedIds] = useState<string[]>([]);
  const [downloadToast, setDownloadToast] = useState(false);
  // Plan configurations
  const planConfig = {
    free: {
      name: 'Free Trial',
      price: '$0',
      period: '7 days',
      included: 15,
      rate: 0.15,
      normalUsed: 8,
      overageUsed: 19
    },
    pro: {
      name: 'Pro',
      price: '$29',
      period: 'mo',
      included: 2000,
      rate: 0.1,
      normalUsed: 847,
      overageUsed: 2180
    }
  };
  const currentConfig = planConfig[planMode];
  const minutesUsed = simOverage ? currentConfig.overageUsed : currentConfig.normalUsed;
  const minutesIncluded = currentConfig.included;
  const overageMinutes = Math.max(0, minutesUsed - minutesIncluded);
  const overageCost = overageMinutes * currentConfig.rate;
  const isOverage = minutesUsed > minutesIncluded;
  // Invoice Data
  const freeNormalInvoices: Invoice[] = [{
    id: 'inv-f1',
    date: 'Jan 28, 2026',
    amount: '$0.00',
    desc: 'Free Trial — No overage',
    minutes: 8,
    included: 15,
    overage: 0,
    planName: 'Free Trial',
    rate: 0.15,
    basePrice: 0
  }, {
    id: 'inv-f2',
    date: 'Dec 28, 2025',
    amount: '$0.00',
    desc: 'Free Trial — No overage',
    minutes: 12,
    included: 15,
    overage: 0,
    planName: 'Free Trial',
    rate: 0.15,
    basePrice: 0
  }, {
    id: 'inv-f3',
    date: 'Nov 28, 2025',
    amount: '$0.00',
    desc: 'Free Trial — No overage',
    minutes: 14,
    included: 15,
    overage: 0,
    planName: 'Free Trial',
    rate: 0.15,
    basePrice: 0
  }, {
    id: 'inv-f4',
    date: 'Oct 28, 2025',
    amount: '$0.00',
    desc: 'Free Trial — No overage',
    minutes: 5,
    included: 15,
    overage: 0,
    planName: 'Free Trial',
    rate: 0.15,
    basePrice: 0
  }];
  const freeOverageInvoices: Invoice[] = [{
    id: 'inv-f1o',
    date: 'Jan 28, 2026',
    amount: '$0.60',
    desc: 'Overage — 4 min × $0.15',
    minutes: 19,
    included: 15,
    overage: 4,
    planName: 'Free Trial',
    rate: 0.15,
    basePrice: 0
  }, {
    id: 'inv-f2o',
    date: 'Dec 28, 2025',
    amount: '$0.00',
    desc: 'Free Trial — No overage',
    minutes: 12,
    included: 15,
    overage: 0,
    planName: 'Free Trial',
    rate: 0.15,
    basePrice: 0
  }, {
    id: 'inv-f3o',
    date: 'Nov 28, 2025',
    amount: '$1.05',
    desc: 'Overage — 7 min × $0.15',
    minutes: 22,
    included: 15,
    overage: 7,
    planName: 'Free Trial',
    rate: 0.15,
    basePrice: 0
  }, {
    id: 'inv-f4o',
    date: 'Oct 28, 2025',
    amount: '$0.00',
    desc: 'Free Trial — No overage',
    minutes: 14,
    included: 15,
    overage: 0,
    planName: 'Free Trial',
    rate: 0.15,
    basePrice: 0
  }];
  const proNormalInvoices: Invoice[] = [{
    id: 'inv-p1',
    date: 'Jan 28, 2026',
    amount: '$29.00',
    desc: 'Pro Plan — Base',
    minutes: 847,
    included: 2000,
    overage: 0,
    planName: 'Pro',
    rate: 0.1,
    basePrice: 29
  }, {
    id: 'inv-p2',
    date: 'Dec 28, 2025',
    amount: '$29.00',
    desc: 'Pro Plan — Base',
    minutes: 1205,
    included: 2000,
    overage: 0,
    planName: 'Pro',
    rate: 0.1,
    basePrice: 29
  }, {
    id: 'inv-p3',
    date: 'Nov 28, 2025',
    amount: '$29.00',
    desc: 'Pro Plan — Base',
    minutes: 1890,
    included: 2000,
    overage: 0,
    planName: 'Pro',
    rate: 0.1,
    basePrice: 29
  }, {
    id: 'inv-p4',
    date: 'Oct 28, 2025',
    amount: '$29.00',
    desc: 'Pro Plan — Base',
    minutes: 950,
    included: 2000,
    overage: 0,
    planName: 'Pro',
    rate: 0.1,
    basePrice: 29
  }];
  const proOverageInvoices: Invoice[] = [{
    id: 'inv-p1o',
    date: 'Jan 28, 2026',
    amount: '$47.00',
    desc: 'Pro Plan + 180 min overage',
    minutes: 2180,
    included: 2000,
    overage: 180,
    planName: 'Pro',
    rate: 0.1,
    basePrice: 29
  }, {
    id: 'inv-p2o',
    date: 'Dec 28, 2025',
    amount: '$29.00',
    desc: 'Pro Plan — Base',
    minutes: 1950,
    included: 2000,
    overage: 0,
    planName: 'Pro',
    rate: 0.1,
    basePrice: 29
  }, {
    id: 'inv-p3o',
    date: 'Nov 28, 2025',
    amount: '$34.50',
    desc: 'Pro Plan + 55 min overage',
    minutes: 2055,
    included: 2000,
    overage: 55,
    planName: 'Pro',
    rate: 0.1,
    basePrice: 29
  }, {
    id: 'inv-p4o',
    date: 'Oct 28, 2025',
    amount: '$29.00',
    desc: 'Pro Plan — Base',
    minutes: 1800,
    included: 2000,
    overage: 0,
    planName: 'Pro',
    rate: 0.1,
    basePrice: 29
  }];
  const invoices = planMode === 'free' ? simOverage ? freeOverageInvoices : freeNormalInvoices : simOverage ? proOverageInvoices : proNormalInvoices;
  const handleDownload = (id: string) => {
    setDownloadedIds(prev => [...prev, id]);
    setDownloadToast(true);
    setTimeout(() => setDownloadToast(false), 2000);
  };
  const toggleSimOverage = () => {
    setSimOverage(!simOverage);
    setSelectedInvoice(null);
  };
  return <div className="h-full flex flex-col p-6 space-y-5 pb-40 overflow-y-auto relative" data-id="element-2384">
      <div className={`absolute top-20 -right-10 w-40 h-40 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/15' : 'bg-vexu/10'}`} data-id="element-2385" />

      {/* Header */}
      <div className="flex items-center justify-between relative z-10" data-id="element-2386">
        <div className="flex items-center gap-3" data-id="element-2387">
          <button onClick={() => onNavigate?.('profile')} className={`p-2 -ml-2 rounded-full border transition-colors ${isLight ? 'text-slate-500 hover:text-slate-900 bg-white/60 backdrop-blur-md border-slate-200' : 'text-zinc-400 hover:text-white bg-white/5 backdrop-blur-md border-white/10'}`} data-id="element-2388">
            <ArrowLeft className="w-5 h-5" data-id="element-2389" />
          </button>
          <h1 className={`text-lg font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2390">
            Usage
          </h1>
        </div>
        <button onClick={toggleSimOverage} className={`px-3 py-1.5 rounded-full text-[11px] font-medium border transition-colors ${simOverage ? isLight ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-amber-500/10 border-amber-500/20 text-amber-400' : isLight ? 'bg-slate-100 border-slate-200 text-slate-600' : 'bg-white/10 border-white/10 text-zinc-300'}`} data-id="element-2391">
          {simOverage ? 'Show Normal' : 'Show Overage'}
        </button>
      </div>

      {/* Plan Toggle (Simulation) */}
      <div className={`flex p-1 rounded-xl relative z-10 ${isLight ? 'bg-slate-100/80 backdrop-blur-md' : 'bg-white/5 backdrop-blur-md'}`} data-id="element-2392">
        <button onClick={() => {
        setPlanMode('free');
        setSelectedInvoice(null);
      }} className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${planMode === 'free' ? isLight ? 'bg-white text-slate-900 shadow-sm' : 'bg-white/10 text-white shadow-sm' : isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-400 hover:text-zinc-200'}`} data-id="element-2393">
          Free Trial
        </button>
        <button onClick={() => {
        setPlanMode('pro');
        setSelectedInvoice(null);
      }} className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${planMode === 'pro' ? isLight ? 'bg-white text-slate-900 shadow-sm' : 'bg-white/10 text-white shadow-sm' : isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-400 hover:text-zinc-200'}`} data-id="element-2394">
          Pro Plan
        </button>
      </div>

      {/* Plan Card */}
      <div className={`backdrop-blur-xl rounded-2xl p-5 shadow-lg border relative z-10 transition-colors duration-500 ${isLight ? 'bg-slate-900 text-white shadow-slate-900/20 border-slate-800' : 'bg-black/60 text-white shadow-black/50 border-white/10'}`} data-id="element-2395">
        <div className="flex justify-between items-start mb-4" data-id="element-2396">
          <div data-id="element-2397">
            <div className="text-vexu font-bold text-[11px] uppercase tracking-wider mb-0.5" data-id="element-2398">
              Current Plan
            </div>
            <h2 className="text-xl font-bold" data-id="element-2399">{currentConfig.name}</h2>
          </div>
          <div className="bg-white/10 backdrop-blur-md px-3 py-1 rounded-full text-xs font-medium border border-white/10" data-id="element-2400">
            {currentConfig.price}/{currentConfig.period}
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm bg-white/5 p-2.5 rounded-xl border border-white/5" data-id="element-2401">
          <Calendar className="w-3.5 h-3.5 text-zinc-400" data-id="element-2402" />
          <span className="text-zinc-400 text-xs" data-id="element-2403">
            {planMode === 'free' ? 'Trial ends Feb 28, 2026' : 'Resets on Mar 1, 2026'}
          </span>
        </div>
      </div>

      {/* Minutes Usage */}
      <div className={`backdrop-blur-xl border rounded-2xl p-5 shadow-sm transition-colors relative z-10 ${isLight ? 'bg-white/80 border-slate-200/60' : 'bg-dark-card/60 border-white/10'}`} data-id="element-2404">
        <div className="flex items-center justify-between mb-1" data-id="element-2405">
          <div className="flex items-center gap-2" data-id="element-2406">
            <Clock className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2407" />
            <span className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2408">
              Minutes Used
            </span>
          </div>
          <span className={`text-sm font-bold ${isOverage ? isLight ? 'text-amber-600' : 'text-amber-400' : isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-2409">
            {minutesUsed.toLocaleString()} / {minutesIncluded.toLocaleString()}{' '}
            min
          </span>
        </div>

        <div className={`h-2.5 rounded-full overflow-hidden mt-3 mb-2 ${isLight ? 'bg-slate-100' : 'bg-white/10'}`} data-id="element-2410">
          <div className={`h-full rounded-full transition-all duration-500 ${isOverage ? 'bg-amber-500' : 'bg-vexu'}`} style={{
          width: `${Math.min(100, minutesUsed / minutesIncluded * 100)}%`
        }} data-id="element-2411" />
        </div>

        <div className="flex items-center justify-between" data-id="element-2412">
          <span className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2413">
            {isOverage ? `${overageMinutes.toLocaleString()} min over limit` : `${(minutesIncluded - minutesUsed).toLocaleString()} min remaining`}
          </span>
          {isOverage && <span className={`text-xs font-bold ${isLight ? 'text-amber-600' : 'text-amber-400'}`} data-id="element-2414">
              +${overageCost.toFixed(2)} overage
            </span>}
        </div>
      </div>

      {/* Overage Warning (when over limit) */}
      <AnimatePresence data-id="element-2415">
        {isOverage && <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} className={`border rounded-2xl p-4 relative z-10 ${isLight ? 'bg-amber-50 border-amber-200' : 'bg-amber-500/10 border-amber-500/20'}`} data-id="element-2416">
            <div className="flex items-start gap-3" data-id="element-2417">
              <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isLight ? 'text-amber-500' : 'text-amber-400'}`} data-id="element-2418" />
              <div className="flex-1" data-id="element-2419">
                <p className={`text-sm font-bold mb-0.5 ${isLight ? 'text-amber-800' : 'text-amber-300'}`} data-id="element-2420">
                  Pay-as-you-go active
                </p>
                <p className={`text-xs leading-relaxed ${isLight ? 'text-amber-700' : 'text-amber-400/80'}`} data-id="element-2421">
                  You've used {overageMinutes.toLocaleString()} min beyond your
                  included {minutesIncluded.toLocaleString()} min. Additional
                  usage is billed at{' '}
                  <span className="font-semibold" data-id="element-2422">
                    ${currentConfig.rate.toFixed(2)}/min
                  </span>
                  .
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Billing History */}
      <div className={`backdrop-blur-xl border rounded-2xl overflow-hidden shadow-sm transition-colors relative z-10 ${isLight ? 'bg-white/80 border-slate-200/60' : 'bg-dark-card/60 border-white/10'}`} data-id="element-2423">
        <div className="p-4 pb-2 flex items-center justify-between" data-id="element-2424">
          <div className="flex items-center gap-2" data-id="element-2425">
            <Receipt className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2426" />
            <span className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2427">
              Billing History
            </span>
          </div>
        </div>
        <div data-id="element-2428">
          {invoices.map((invoice, i) => {
          const isDownloaded = downloadedIds.includes(invoice.id);
          return <div key={invoice.id} className={`flex items-center justify-between ${i < invoices.length - 1 ? isLight ? 'border-b border-slate-100' : 'border-b border-white/5' : ''}`} data-id="element-2429">
                <button onClick={() => setSelectedInvoice(invoice)} className={`flex-1 min-w-0 px-4 py-3 text-left transition-colors ${isLight ? 'hover:bg-slate-50' : 'hover:bg-white/[0.03]'}`} data-id="element-2430">
                  <div className="flex items-center gap-2" data-id="element-2431">
                    <span className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2432">
                      {invoice.amount}
                    </span>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${isLight ? 'bg-green-50 text-green-600' : 'bg-green-500/10 text-green-400'}`} data-id="element-2433">
                      Paid
                    </span>
                  </div>
                  <span className={`text-[11px] block mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2434">
                    {invoice.desc}
                  </span>
                  <span className={`text-[10px] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2435">
                    {invoice.date}
                  </span>
                </button>
                <button onClick={() => handleDownload(invoice.id)} className={`p-2 mr-2 rounded-lg transition-colors ${isDownloaded ? isLight ? 'text-vexu-600 bg-vexu/10' : 'text-vexu bg-vexu/10' : isLight ? 'text-slate-400 hover:bg-slate-100 hover:text-slate-600' : 'text-zinc-500 hover:bg-white/5 hover:text-zinc-300'}`} data-id="element-2436">
                  {isDownloaded ? <CheckCircle2 className="w-4 h-4" data-id="element-2437" /> : <Download className="w-4 h-4" data-id="element-2438" />}
                </button>
              </div>;
        })}
        </div>
      </div>

      {/* Upgrade CTA (Only for Free Trial) */}
      {planMode === 'free' && <div className={`backdrop-blur-xl rounded-2xl p-5 border shadow-sm transition-colors relative z-10 ${isLight ? 'bg-vexu/5 border-vexu/20' : 'bg-vexu/5 border-vexu/20'}`} data-id="element-2439">
          <div className="flex items-start gap-3 mb-4" data-id="element-2440">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-vexu/10 text-vexu-600' : 'bg-vexu/10 text-vexu'}`} data-id="element-2441">
              <TrendingUp className="w-5 h-5" data-id="element-2442" />
            </div>
            <div data-id="element-2443">
              <h3 className={`text-sm font-bold mb-0.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2444">
                Upgrade to Pro
              </h3>
              <p className={`text-xs leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2445">
                Get 2,000 minutes, custom AI instructions, multi-lingual
                support, and language detection for $29/mo.
              </p>
            </div>
          </div>
          <button onClick={() => onNavigate?.('planSelection')} className="w-full py-3 rounded-xl text-sm font-bold text-center bg-vexu text-white flex items-center justify-center gap-2 hover:bg-vexu-600 transition-colors" data-id="element-2446">
            <Zap className="w-4 h-4" data-id="element-2447" />
            Upgrade Plan
          </button>
        </div>}

      {/* Invoice Detail Modal */}
      <AnimatePresence data-id="element-2448">
        {selectedInvoice && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-end justify-center p-4" data-id="element-2449">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedInvoice(null)} data-id="element-2450" />
            <motion.div initial={{
          y: 200,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 200,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }} className={`relative w-full max-w-sm rounded-3xl p-6 space-y-4 ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-2451">
              <div className="flex items-start justify-between" data-id="element-2452">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-2453">
                  <FileText className={`w-6 h-6 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2454" />
                </div>
                <button onClick={() => setSelectedInvoice(null)} className={`p-1.5 rounded-full ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/10'}`} data-id="element-2455">
                  <X className="w-5 h-5" data-id="element-2456" />
                </button>
              </div>

              <div data-id="element-2457">
                <h3 className={`text-lg font-bold mb-0.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2458">
                  Invoice
                </h3>
                <p className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2459">
                  {selectedInvoice.date}
                </p>
              </div>

              <div className={`rounded-xl p-4 space-y-3 ${isLight ? 'bg-slate-50' : 'bg-white/[0.03]'}`} data-id="element-2460">
                <div className="flex justify-between" data-id="element-2461">
                  <span className={`text-sm ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2462">
                    Plan
                  </span>
                  <span className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2463">
                    {selectedInvoice.planName}
                  </span>
                </div>

                {selectedInvoice.basePrice > 0 && <div className="flex justify-between" data-id="element-2464">
                    <span className={`text-sm ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2465">
                      Base Price
                    </span>
                    <span className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2466">
                      ${selectedInvoice.basePrice.toFixed(2)}
                    </span>
                  </div>}

                <div className="flex justify-between" data-id="element-2467">
                  <span className={`text-sm ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2468">
                    Minutes Used
                  </span>
                  <span className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2469">
                    {selectedInvoice.minutes.toLocaleString()} min
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-2470">
                  <span className={`text-sm ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2471">
                    Included
                  </span>
                  <span className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2472">
                    {selectedInvoice.included.toLocaleString()} min
                  </span>
                </div>
                {selectedInvoice.overage > 0 && <div className="flex justify-between" data-id="element-2473">
                    <span className={`text-sm ${isLight ? 'text-amber-600' : 'text-amber-400'}`} data-id="element-2474">
                      Overage
                    </span>
                    <span className={`text-sm font-bold ${isLight ? 'text-amber-600' : 'text-amber-400'}`} data-id="element-2475">
                      {selectedInvoice.overage.toLocaleString()} min × $
                      {selectedInvoice.rate.toFixed(2)}
                    </span>
                  </div>}
                <div className={`pt-2 border-t flex justify-between ${isLight ? 'border-slate-200' : 'border-white/10'}`} data-id="element-2476">
                  <span className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2477">
                    Total
                  </span>
                  <span className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2478">
                    {selectedInvoice.amount}
                  </span>
                </div>
              </div>

              <button onClick={() => {
            handleDownload(selectedInvoice.id);
            setSelectedInvoice(null);
          }} className="w-full py-3 rounded-xl font-semibold text-sm bg-vexu text-white hover:bg-vexu-600 transition-colors flex items-center justify-center gap-2" data-id="element-2479">
                <Download className="w-4 h-4" data-id="element-2480" />
                Download Invoice
              </button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Download Toast */}
      <AnimatePresence data-id="element-2481">
        {downloadToast && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} className="fixed bottom-28 left-1/2 -translate-x-1/2 z-50" data-id="element-2482">
            <div className={`flex items-center gap-2.5 px-5 py-3 rounded-2xl shadow-lg backdrop-blur-xl ${isLight ? 'bg-slate-900 text-white' : 'bg-white text-black'}`} data-id="element-2483">
              <CheckCircle2 className="w-4 h-4 text-vexu" data-id="element-2484" />
              <span className="text-sm font-medium" data-id="element-2485">Invoice downloaded</span>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}